import * as React from 'react';

import cx from 'classnames';
import { filter as lodashFilter, isEmpty } from 'lodash';

import { ButtonColor, ButtonLevel } from 'components/Button';
import CheckboxList, {
  CheckboxListOptions,
  Props as CheckboxListProps,
} from 'components/CheckboxList';
import Field from 'components/Field';
import Heading from 'components/Heading';
import Layer from 'components/Layer';
import MenuButton from 'components/MenuButton';
import Radio, { Props as RadioProps } from 'components/Radio';
import SearchExpanding from 'components/SearchExpanding';
import Select, { Props as SelectProps } from 'components/Select';
import TextWithIcon from 'components/TextWithIcon';
import { GroupStatus } from 'groups/components/GroupList/GroupListTable';

import './ListActions.scss';

// we need to explicitly add any string enums that could potentially
// get passed in as a filter value, because typescript doesn't accept
// a string enum value as an acceptable substitute for a generic string
type StringOrValidEnumString = string | GroupStatus;

type ListActionsFilter = {
  defaultValue?: string;
  inputProps?: CheckboxListProps | RadioProps | SelectProps;
  label?: any;
  name: string;
  onChange?: Function;
  onClick?: Function;
  options?: any;
  title: string;
  type: 'checkbox' | 'radio' | 'select';
};

export type ListActionsCheckboxFilterState = StringOrValidEnumString[];
export type ListActionsRadioFilterState = StringOrValidEnumString;
export type ListActionsSelectFilterState = StringOrValidEnumString;

type ListActionsItemFilterState =
  | ListActionsCheckboxFilterState
  | ListActionsRadioFilterState
  | ListActionsSelectFilterState;

export type ListActionsFilterState = {
  [filter: string]: ListActionsItemFilterState;
};

type Props = {
  filterButtonFace?: React.ReactElement;
  initialActiveFilters?: ListActionsFilterState;
  onFilterChange?: Function;
  onQueryChange: Function;
  onQueryClick?: () => void;
  primaryFilters?: Array<ListActionsFilter>;
  searchPlaceholder?: string;
  showDropDownWhileSearchActive: boolean;
  secondaryFilters?: Array<ListActionsFilter> | null;
};

type State = {
  activeFilters: ListActionsFilterState;
  secondaryFilterActive: boolean;
};

export default class ListActions extends React.Component<Props, State> {
  static defaultProps = {
    filterButtonFace: 'Filter',
    showDropDownWhileSearchActive: false,
  };

  state: State = {
    activeFilters: {},
    secondaryFilterActive: false,
  };

  componentWillMount() {
    const { initialActiveFilters } = this.props;
    this.setState({ activeFilters: initialActiveFilters || {} });
  }

  handleFilterChange = (value: ListActionsItemFilterState, name: string, defaultValue?: string) => {
    const { onFilterChange, secondaryFilters } = this.props;

    this.setState(
      prevState => ({
        activeFilters: {
          ...prevState.activeFilters,
          [name]: value,
        },
      }),
      () => {
        const { activeFilters } = this.state;

        this.setState({
          secondaryFilterActive: !isEmpty(
            lodashFilter(
              secondaryFilters,
              item =>
                item.name === name &&
                Object.keys(activeFilters).includes(name) &&
                !isEmpty(activeFilters[name]) &&
                value !== defaultValue
            )
          ),
        });

        if (onFilterChange) {
          onFilterChange(activeFilters);
        }
      }
    );
  };

  handleQueryChange = (value: string) => {
    const { onQueryChange } = this.props;

    onQueryChange(value);
  };

  renderCheckbox = (
    name: string,
    inputProps: CheckboxListProps | null | undefined,
    options: CheckboxListOptions
  ) => {
    const { activeFilters } = this.state;
    const { [name]: filter } = activeFilters;

    return (
      <CheckboxList
        {...inputProps}
        defaultChecked={filter as ListActionsCheckboxFilterState}
        options={options}
        onChange={e => this.handleFilterChange(e, name)}
        data-test="filter-checkbox-list"
      />
    );
  };

  renderRadioBox = (name: string, inputProps: RadioProps | null | undefined, item: any) => {
    const { activeFilters } = this.state;
    const { [name]: filter } = activeFilters;
    const { display, label, value } = item;

    return (
      <Radio
        {...inputProps}
        label={display || label}
        name={name}
        key={label}
        checked={filter === value}
        onChange={() => this.handleFilterChange(value, name)}
        data-test="filter-radio-input"
      />
    );
  };

  renderSelect = (
    name: string,
    inputProps: SelectProps | null | undefined,
    defaultValue?: string
  ) => {
    const { activeFilters } = this.state;
    const { [name]: filter } = activeFilters;

    return (
      <Select
        {...inputProps}
        searchable={false}
        value={filter}
        data-test="filter-select"
        onChange={(value: string) => this.handleFilterChange(value, name, defaultValue)}
      />
    );
  };

  renderFilters = (filters: Array<ListActionsFilter>): Array<React.ReactElement<Field>> =>
    filters.map((filter, index) => {
      const { defaultValue = '', inputProps, name, options, title, type } = filter;

      return (
        <Field flush={index === filters.length - 1} key={name}>
          <Heading level="3">{title}</Heading>
          {type === 'checkbox' &&
            options &&
            this.renderCheckbox(name, inputProps as CheckboxListProps, options)}
          {type === 'radio' &&
            options &&
            options.map((item: any) => this.renderRadioBox(name, inputProps as RadioProps, item))}
          {type === 'select' && this.renderSelect(name, inputProps as SelectProps, defaultValue)}
        </Field>
      );
    });

  render() {
    const {
      filterButtonFace,
      onQueryClick,
      primaryFilters,
      searchPlaceholder,
      secondaryFilters,
      showDropDownWhileSearchActive,
    } = this.props;
    const { secondaryFilterActive } = this.state;

    const buttonProps: {
      color: ButtonColor;
      level: ButtonLevel;
    } = {
      color: ButtonColor.polarBear,
      level: 'secondary',
    };

    const getDropdown = (forceRedraw: boolean = false) => {
      return (
        primaryFilters && (
          <MenuButton
            // done to force respositioninings.
            key={forceRedraw ? new Date().getTime() : undefined}
            {...buttonProps}
            hideLayerOnScroll
            menu={
              <Layer
                // done to force respositioninings.
                key={forceRedraw ? new Date().getTime() : undefined}
                className="ListActions-dropdown"
                data-test="filter-menu">
                <div
                  className={cx(
                    'ListActions-dropdown-section ListActions-dropdown-section--primary',
                    {
                      'ListActions-dropdown-section--with-secondary':
                        secondaryFilters && secondaryFilters.length > 0,
                    }
                  )}>
                  {this.renderFilters(primaryFilters)}
                </div>
                {secondaryFilters &&
                  secondaryFilters.length > 0 && (
                    <div className="ListActions-dropdown-section">
                      {this.renderFilters(secondaryFilters)}
                    </div>
                  )}
              </Layer>
            }
            data-test="filter-button">
            <TextWithIcon
              appendIcon
              icon={secondaryFilterActive ? 'filter-active' : 'filter'}
              iconClassName="ListActions-filterButton-icon"
              iconColor={secondaryFilterActive ? 'brand' : 'currentColor'}
              iconSize="xlarge"
              gutter="xxsmall"
              data-test="filter-button-text">
              {filterButtonFace}
            </TextWithIcon>
          </MenuButton>
        )
      );
    };

    return (
      <SearchExpanding
        buttonProps={buttonProps}
        inputProps={{
          placeholder: searchPlaceholder,
          'data-test': 'search-input',
        }}
        className="ListActions"
        onCancel={() => this.handleQueryChange('')}
        onChange={this.handleQueryChange}
        onClick={onQueryClick}
        childrenWhileActive={showDropDownWhileSearchActive ? getDropdown : undefined}>
        {getDropdown()}
      </SearchExpanding>
    );
  }
}
